import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, NumberInput, NumberInputField, Select, Box } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { resolveFieldCode } from '../../core/commands/fields';
import { runInteraction, PT } from '../../core/commands/runners/lib';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';

/**
 * FIELD dialog (Faz 8.8, OCS field_dialog parity — pragmatic categories):
 * Date & Time (4), Document (filename), Object (live property of the
 * selected entity), Formula (AcExpr + parameter table), System Variable.
 * Insert embeds `%<...>%` in an mtext at a picked point (the resolved value
 * renders live through the field cache).
 */

type FieldCategory = 'dateTime' | 'document' | 'object' | 'formula' | 'sysvar';

interface FieldOption {
    /** Full code body between %<...>%. */
    code: string;
    label: string;
}

/** Object property list per selected entity type (AcObjProp subset). */
function objectOptions(type: string): FieldOption[] {
    switch (type) {
        case 'line':
            return [
                { code: 'Length', label: 'Length' },
                { code: 'StartPoint', label: 'Start point' },
                { code: 'EndPoint', label: 'End point' },
            ];
        case 'circle':
            return [
                { code: 'Area', label: 'Area' },
                { code: 'Diameter', label: 'Diameter' },
                { code: 'Radius', label: 'Radius' },
                { code: 'Circumference', label: 'Circumference' },
            ];
        case 'arc':
            return [
                { code: 'Length', label: 'Arc length' },
                { code: 'Radius', label: 'Radius' },
            ];
        case 'polyline':
        case 'rectangle':
        case 'polygon':
        case 'hatch':
            return [
                { code: 'Area', label: 'Area' },
                { code: 'Length', label: 'Perimeter/Length' },
            ];
        default:
            return [];
    }
}

export function FieldDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'field');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const entities = useAppStore((s) => s.entities);
    const activeLayerId = useAppStore((s) => s.activeLayerId);
    const [category, setCategory] = useState<FieldCategory>('dateTime');
    const [optionIdx, setOptionIdx] = useState(0);
    const [precision, setPrecision] = useState(2);
    const [formula, setFormula] = useState('');

    const host = selectedIds.map((id) => entities[id]).find(Boolean);
    const objOpts = host ? objectOptions(host.type) : [];
    const opts: FieldOption[] = (() => {
        switch (category) {
            case 'dateTime':
                return [
                    { code: '\\AcVar date', label: t('ui.cad.field.cats.dateTime') + ' (short)' },
                    { code: '\\AcVar cdate', label: t('ui.cad.field.cats.dateTime') + ' (+time)' },
                    { code: '\\AcVar today', label: 'Today' },
                    { code: '\\AcVar savedate', label: 'Savedate' },
                ];
            case 'document':
                return [
                    { code: '\\AcVar dwgname', label: 'Drawing name' },
                    { code: '\\AcVar dwgtitle', label: 'Drawing title' },
                ];
            case 'object':
                return objOpts.map((o) => ({ code: `\\AcObjProp Object(${host?.id ?? ''}).${o.code}`, label: o.label }));
            case 'formula':
                return [{ code: `\\AcExpr (${formula || '0'})`, label: formula || '0' }];
            case 'sysvar':
                return [
                    { code: '\\AcVar clayer', label: 'CLAYER (current layer)' },
                    { code: '\\AcVar cannoscale', label: 'CANNOSCALE' },
                    { code: '\\AcVar textsize', label: 'TEXTSIZE' },
                    { code: '\\AcVar userr1', label: 'USERR1' },
                ];
            default:
                return [];
        }
    })();
    const selected = opts[Math.min(optionIdx, Math.max(0, opts.length - 1))];

    // Numeric fields carry the dialog precision in the code (\f "%lu<digits>").
    const codeWithPrecision = (() => {
        if (!selected) return '';
        const numeric = category === 'object'
            || (category === 'formula')
            || selected.code.includes('textsize')
            || selected.code.includes('userr');
        return numeric ? `${selected.code} \\f "%lu${precision}"` : selected.code;
    })();
    const preview = selected
        ? resolveFieldCode(codeWithPrecision, host?.id ?? null) ?? '—'
        : '—';

    const insert = () => {
        if (!selected) return;
        onClose();
        const content = `%<${codeWithPrecision}>%`;
        runInteraction({
            steps: [PT('FIELD: text placement point')],
            complete: (pts) => {
                const p = pts[0];
                undoableAdd([makeEntity('mtext', `Field-${Date.now()}`, {
                    content, position: [p.x, p.y, p.z],
                    styleId: 'standard', rotation: 0, multiline: true,
                }, activeLayerId)]);
                return `FIELD: field inserted (${preview}).`;
            },
        });
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.field.title')}
            size="md"
            footer={
                <HStack justify="flex-end" w="full" spacing={2}>
                    <DialogButton variant="primary" onClick={insert} isDisabled={!selected || category === 'object' && !host}>
                        {t('ui.cad.field.insert')}
                    </DialogButton>
                    <DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>
                </HStack>
            }
        >
            <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.field.category')}</Text>
                    <Select
                        size="sm" w="220px" value={category}
                        onChange={(e) => { setCategory(e.target.value as FieldCategory); setOptionIdx(0); }}
                    >
                        <option value="dateTime">{t('ui.cad.field.cats.dateTime')}</option>
                        <option value="document">{t('ui.cad.field.cats.document')}</option>
                        <option value="object">{t('ui.cad.field.cats.object')}</option>
                        <option value="formula">{t('ui.cad.field.cats.formula')}</option>
                        <option value="sysvar">{t('ui.cad.field.cats.sysvar')}</option>
                    </Select>
                </HStack>

                {category === 'object' && (
                    <Text fontSize="xs" color="var(--color-text-secondary)">
                        {host ? `${host.type} (${host.id.slice(0, 12)}…)` : t('ui.cad.field.pickHint')}
                    </Text>
                )}
                {category === 'formula' && (
                    <Input
                        size="sm" value={formula}
                        placeholder="Width*2 + 10  (Faz 6 parameters work)"
                        bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                        onChange={(e) => setFormula(e.target.value)}
                    />
                )}
                {category !== 'formula' && (
                    <Select
                        size="sm" value={optionIdx}
                        onChange={(e) => setOptionIdx(Number(e.target.value))}
                        isDisabled={opts.length === 0}
                    >
                        {opts.map((o, i) => <option key={o.code} value={i}>{o.label}</option>)}
                    </Select>
                )}

                <HStack justify="space-between">
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.field.precision')}</Text>
                    <NumberInput size="sm" w="90px" value={precision} min={0} max={8}
                        onChange={(_, v) => setPrecision(Number.isFinite(v) ? Math.max(0, Math.min(8, v)) : precision)}>
                        <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                    </NumberInput>
                </HStack>

                <Box px={2} py={1} bg="bg.active" borderRadius="sm">
                    <Text fontSize="xs" color="var(--color-text-secondary)">{t('ui.cad.field.preview')}</Text>
                    <Text fontSize="sm" fontFamily="mono">{preview}</Text>
                </Box>
                <Text fontSize="xs" color="var(--color-text-secondary)" fontFamily="mono" noOfLines={1}>
                    {codeWithPrecision ? `%<${codeWithPrecision}>%` : t('ui.cad.field.emptyHint')}
                </Text>
            </VStack>
        </AppDialog>
    );
}
